<template>
  <div
    v-if="imageLink"
    tabindex="0"
    class="w-[44px] h-[44px] rounded-10 relative shrink-0 group"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @paste.prevent="setAvatarFromClipboard"
  >
    <img
      :src="getAuthenticatedMediaUrl(imageLink) || ''"
      class="rounded-10 block h-full w-full object-full text-center"
      :alt="$t('common.avatar')"
    >
    <button
      class="
        absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2
        text-white group-hover:block
      "
      @click="$emit('clearAvatar')"
    >
      <span class="sr-only select-none">{{ $t('common.removeSelectedGroupAvatar') }}</span>
      <wui-icon name="closeRegularMdM" />
    </button>
  </div>

  <button
    v-else
    class="relative shrink-0 p-3 bg-bg-base-bg4 rounded-12 transition-all hover:bg-bg-base-bg6"
    :title="$t('mainAside.youCanUploadImageByClick')"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @paste.prevent="setAvatarFromClipboard"
  >
    <label
      v-if="!tauriMode()"
      class="absolute inset-0 cursor-pointer"
    >
      <input
        type="file"
        accept="image/*"
        class="hidden"
        @change="setAvatar"
      >
    </label>

    <label
      v-else
      class="absolute inset-0 cursor-pointer"
      @click="setAvatar"
    />
    <wui-icon
      name="cameraM"
      class="text-fg-base-fg22"
    />
  </button>
</template>

<script lang="ts" setup>
import { WuiIcon, useRenderNotifier } from '@wui/common-library'
import { ref, watchEffect, PropType } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { usePasteImageOnHover } from '@/shared/composables/use-paste-image-on-hover'
import { MAX_AVATAR_SIZE_IN_BYTES } from '@/shared/composables/web-dav'
import { compressAvatarFile } from '@/shared/lib/compress-avatar-image'
import { createFile } from '@/shared/utils/convert-uploading-files'
import { tauriMode } from '@/shared/utils/import-meta'
import { getAuthenticatedMediaUrl } from '@/shared/utils/url'

const props = defineProps({
  avatarImage: {
    type: Object as PropType<File | string>,
    required: false,
    default: undefined,
  },
})

const emit = defineEmits<{
  (event: 'setAvatar', value: File | string | undefined): void;
  (event: 'clearAvatar'): void;
}>()

const imageLink = ref()

const { notifyError } = useRenderNotifier()
const { t } = useLocalization()

const setAvatarFromFile = async (file?: File) => {
  if (!file)
    return

  if (file.type.includes('image') && !file.type.includes('svg')) {
    if (file.size > MAX_AVATAR_SIZE_IN_BYTES) {
      notifyError(t('common.attachedFileTooLargeDescription', { size: `${MAX_AVATAR_SIZE_IN_BYTES / 1e6} ${t('common.mb')}` }))
      return
    }
    emit('setAvatar', await compressAvatarFile(file))
    return
  }

  notifyError(t('mainAside.failedToLoadAvatarBecauseOfExtension'))
}

const setAvatar = async (evt: Event) => {
  if (!tauriMode()) {
    const target = evt.target as HTMLInputElement
    const file = target.files?.[0]
    setAvatarFromFile(file)
  } else {
    const { open } = await import('@tauri-apps/plugin-dialog')
    const selectedFiles = await open({
      multiple: false,
      filters: [{
        name: 'Image',
        extensions: ['png','jpeg', 'jpg', 'svg', 'bmp', 'gif', 'tif'],
      }],
    })
    if (selectedFiles) {
      emit('setAvatar', selectedFiles.toString())
    }
  }
}

const setAvatarFromClipboard = (evt: ClipboardEvent) => {
  const file = evt.clipboardData?.files?.[0]
  if (!file) return

  setAvatarFromFile(file)
}

const { onMouseEnter, onMouseLeave } = usePasteImageOnHover({
  onImagePaste: (file) => {
    setAvatarFromFile(file)
  },
})

watchEffect(() => {
  if (props.avatarImage) {
    if (!tauriMode()) {
      const reader = new FileReader()
      reader.readAsDataURL(props.avatarImage as File)
      reader.onload = () => {
        imageLink.value = reader.result
      }
    } else {
      createFile(props.avatarImage as string).then((result) => {
        const reader = new FileReader()
        reader.readAsDataURL(result)
        reader.onload = () => {
          imageLink.value = reader.result
        }
      })
    }
  } else {
    imageLink.value = null
  }
})
</script>

<style scoped>
@reference "../../../app/assets/tailwind.css";

div::before {
  @apply content-[''] absolute cursor-pointer bg-black opacity-40 rounded-10 hidden;
  inset: 0;
}

div:hover::before {
  display: block;
}
</style>
